import type { AriaAttributes, ReactNode } from "react";
import { X } from "lucide-react";

import { initials } from "../lib/format";

/** Merge conditional class names. One helper rather than a clsx dependency. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section
      className={cx(
        "rounded-lg border border-slate-200 bg-white shadow-xs",
        padded && "p-4",
        className,
      )}
    >
      {children}
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/**
 * The heading inside a `Card`. Every card title is the same size, weight and
 * spacing, with the optional link or button pinned to the right.
 */
export function CardTitle({
  children,
  action,
  className,
}: {
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("mb-2 flex items-center justify-between gap-2", className)}>
      <h2 className="min-w-0 text-sm font-semibold text-slate-900">{children}</h2>
      {action ? (
        <div className="flex shrink-0 items-center gap-1.5 text-xs">{action}</div>
      ) : null}
    </div>
  );
}

/**
 * Stage colours. One place, so a lead and an active client never end up the
 * same shade of grey because two screens picked differently.
 */
const STAGE_TONE: Record<string, string> = {
  lead: "bg-amber-50 text-amber-700 ring-amber-600/20",
  prospect: "bg-sky-50 text-sky-700 ring-sky-600/20",
  active: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  inactive: "bg-slate-100 text-slate-600 ring-slate-500/20",
  lost: "bg-rose-50 text-rose-700 ring-rose-600/20",
  vendor: "bg-violet-50 text-violet-700 ring-violet-600/20",
};

export function StageBadge({ stage }: { stage?: string | null }) {
  if (!stage) return <Badge tone="slate">Unknown</Badge>;
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset capitalize",
        STAGE_TONE[stage] ?? "bg-slate-100 text-slate-600 ring-slate-500/20",
      )}
    >
      {stage}
    </span>
  );
}

export function Badge({
  children,
  tone = "slate",
}: {
  children: ReactNode;
  tone?: "slate" | "emerald" | "amber" | "sky" | "rose" | "violet";
}) {
  const tones: Record<string, string> = {
    slate: "bg-slate-100 text-slate-600 ring-slate-500/20",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    amber: "bg-amber-50 text-amber-700 ring-amber-600/20",
    sky: "bg-sky-50 text-sky-700 ring-sky-600/20",
    rose: "bg-rose-50 text-rose-700 ring-rose-600/20",
    violet: "bg-violet-50 text-violet-700 ring-violet-600/20",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

export function Button({
  children,
  onClick,
  variant = "secondary",
  size = "md",
  type = "button",
  disabled,
  title,
  className,
  "aria-label": ariaLabel,
  "aria-haspopup": ariaHaspopup,
  "aria-expanded": ariaExpanded,
}: {
  children: ReactNode;
  onClick?: (() => void) | undefined;
  variant?: "primary" | "secondary" | "ghost" | "outline" | undefined;
  size?: "sm" | "md" | undefined;
  type?: "button" | "submit" | undefined;
  disabled?: boolean | undefined;
  title?: string | undefined;
  className?: string | undefined;
  "aria-label"?: string | undefined;
  "aria-haspopup"?: AriaAttributes["aria-haspopup"];
  "aria-expanded"?: AriaAttributes["aria-expanded"];
}) {
  const variants: Record<string, string> = {
    primary: "bg-slate-900 text-white hover:bg-slate-800",
    secondary: "bg-white text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
    ghost: "text-slate-600 hover:bg-slate-100",
    outline: "bg-transparent text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
  };
  const sizes: Record<string, string> = {
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-1.5 text-sm",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      aria-haspopup={ariaHaspopup}
      aria-expanded={ariaExpanded}
      className={cx(
        "inline-flex items-center gap-1.5 rounded-md font-medium transition",
        "disabled:cursor-not-allowed disabled:opacity-50",
        sizes[size],
        variants[variant],
        className,
      )}
    >
      {children}
    </button>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon,
}: {
  title: string;
  body: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 px-4 py-8 text-center">
      {icon ? <div className="text-slate-300">{icon}</div> : null}
      <p className="text-sm font-medium text-slate-900">{title}</p>
      <p className="max-w-sm text-sm text-slate-500">{body}</p>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx("animate-pulse rounded bg-slate-200", className)} />;
}

/**
 * A consistent employee/contact thumbnail: the photo when there is one, the
 * initials otherwise. One treatment everywhere so a row, a header and a chart
 * node never disagree about what a person looks like.
 */
export function Avatar({
  name,
  src,
  size = 40,
}: {
  name: string;
  src?: string | null;
  size?: number;
}) {
  const dimension = { width: size, height: size };
  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={dimension}
        loading="lazy"
        className="shrink-0 rounded-full object-cover ring-1 ring-slate-200"
      />
    );
  }
  return (
    <span
      style={dimension}
      aria-hidden="true"
      className="grid shrink-0 place-items-center rounded-full bg-slate-100 text-xs font-semibold uppercase text-slate-500 ring-1 ring-inset ring-slate-200"
    >
      {initials(name)}
    </span>
  );
}

export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="ml-auto h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

/**
 * A labelled progress bar for a plan limit. `value` is the usage, `limit` the
 * cap; `limit === null` means unlimited, which is drawn as a flat track rather
 * than a full bar, because "100% used" would be a lie.
 */
export function LimitBar({
  label,
  value,
  limit,
  format,
}: {
  label: string;
  value: number;
  /** null/undefined means unlimited, which reads differently from zero left. */
  limit: number | null | undefined;
  format: (n: number) => string;
}) {
  const cap = limit ?? null;
  const pct = cap ? Math.min(100, Math.round((value / cap) * 100)) : 0;
  const tone = !cap
    ? "bg-slate-300"
    : pct >= 90
      ? "bg-rose-500"
      : pct >= 70
        ? "bg-amber-500"
        : "bg-emerald-500";

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4 text-sm">
        <span className="text-slate-600">{label}</span>
        <span className="tabular-nums text-slate-900">
          {format(value)}
          <span className="text-slate-400">
            {cap === null ? " · unlimited" : ` / ${format(cap)}`}
          </span>
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={cx("h-full rounded-full", tone)} style={{ width: `${cap ? pct : 100}%` }} />
      </div>
    </div>
  );
}

/**
 * A centred modal dialog. The backdrop is a real `<button>` so keyboard users
 * can dismiss with Escape (handled by the consumer) or by clicking outside;
 * the panel is `role="dialog" aria-modal="true"` with a labelled close button.
 */
export function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        aria-label={`Close ${title}`}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx(
          "relative z-10 max-h-[90vh] w-full overflow-y-auto rounded-lg bg-white shadow-2xl",
          wide ? "max-w-2xl" : "max-w-md",
        )}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </header>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}